import { useRef, useState } from "react";
import {
  Avatar, ago, api, can, CONTENT_STATUS, Drawer, Empty, fmtDate, fmtDateTime, Loading, Pill, PLATFORM_LABEL, PLATFORMS, Sample, setQuery, StatusPill, todayYmd, useLoad, useMe, useRoute,
} from "./lib";
import { AdviceTake, DeptPage } from "./dept";
import { FigmaDesigns } from "./figma";
import { ReleaseLink } from "./product";
import { NewTask, TaskDrawer, TaskRow } from "./work";

const FLOW = ["draft", "in_review", "approved", "scheduled", "published"];
const BOARD_COLS = ["draft", "in_review", "changes_requested", "approved", "scheduled", "publish_failed", "published"];

export function Marketing() {
  const { me } = useMe();
  const route = useRoute();
  const tab = route.q.get("tab") || "content";
  const view = route.q.get("view") || "board";
  const toolbar = (
    <div className="row between">
      <div className="tabs" role="tablist">
        <button className={tab === "content" ? "on" : ""} onClick={() => setQuery({ tab: null })}>Content</button>
        {can(me, "campaigns.view") && <button className={tab === "campaigns" ? "on" : ""} onClick={() => setQuery({ tab: "campaigns" })}>Campaigns</button>}
        <button className={tab === "designs" ? "on" : ""} onClick={() => setQuery({ tab: "designs" })}>Designs</button>
      </div>
      {tab === "content" && (
        <div className="tabs" role="tablist" aria-label="View">
          {["calendar", "board", "list"].map((v) => (
            <button key={v} className={view === v ? "on" : ""} onClick={() => setQuery({ view: v })}>{v[0]!.toUpperCase() + v.slice(1)}</button>
          ))}
        </div>
      )}
    </div>
  );
  return (
    <DeptPage area="marketing" take={<MarketingTake />} toolbar={toolbar}>
      {tab === "campaigns" ? <Campaigns /> : tab === "designs" ? <FigmaDesigns area="marketing" /> : <ContentViews view={view} />}
      {route.q.get("content") && <ContentDrawer id={route.q.get("content")!} onClose={() => setQuery({ content: null })} />}
      {route.q.get("task") && <TaskDrawer id={route.q.get("task")!} onClose={() => setQuery({ task: null })} onChange={() => {}} />}
    </DeptPage>
  );
}

function ContentViews({ view }: { view: string }) {
  const { me } = useMe();
  const route = useRoute();
  const { data, error, reload } = useLoad<any[]>("/api/content", [route.q.get("content")]);
  const [adding, setAdding] = useState(false);
  const open = (id: string) => setQuery({ content: id });
  return (
    <>
      <div className="row between">
        <p className="muted small" style={{ margin: 0 }}>
          Publishing is recorded by hand until a social platform is connected (Stage 3). Times are Central.
        </p>
        <div className="row">
          {can(me, "export.data") && <a className="btn" href="/api/export/content.csv">Export CSV</a>}
          {can(me, "content.edit") && <button className="btn primary" onClick={() => setAdding(true)}>New content</button>}
        </div>
      </div>
      {!data ? (
        <Loading error={error} />
      ) : view === "calendar" ? (
        <Calendar items={data} onOpen={open} />
      ) : view === "list" ? (
        <section className="card" style={{ padding: 0 }}>
          {data.length ? (
            <div className="scroll-x">
              <table className="t">
                <thead><tr><th>Title</th><th>Status</th><th className="hide-sm">Owner</th><th className="hide-sm">Campaign</th><th className="hide-sm">Platforms</th><th>Scheduled</th></tr></thead>
                <tbody>
                  {data.map((c) => (
                    <tr key={c.id} className="clickable" onClick={() => open(c.id)}>
                      <td><span className="row" style={{ gap: 6 }}>{c.title}<Sample on={c.is_sample} /></span></td>
                      <td><StatusPill s={c.status} map={CONTENT_STATUS} /></td>
                      <td className="hide-sm">{c.owner_name}</td>
                      <td className="hide-sm">{c.campaign_name || <span className="muted">None</span>}</td>
                      <td className="hide-sm">{c.platforms.map((p: string) => PLATFORM_LABEL[p]).join(", ")}</td>
                      <td>{fmtDateTime(c.scheduled_at) || <span className="muted">Not set</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ padding: 20 }}><Empty>No content yet.</Empty></div>
          )}
        </section>
      ) : (
        <div className="board">
          {BOARD_COLS.map((s) => {
            const items = data.filter((c) => c.status === s);
            return (
              <div key={s} className="col">
                <h3>{CONTENT_STATUS[s]!.label}<span className="muted">{items.length}</span></h3>
                {items.map((c) => (
                  <div key={c.id} className="tile" onClick={() => open(c.id)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && open(c.id)}>
                    {c.thumb_id && <img className="thumb" src={`/api/attachments/${c.thumb_id}`} alt="" loading="lazy" />}
                    <div className="row between" style={{ gap: 6 }}><strong className="small">{c.title}</strong><Sample on={c.is_sample} /></div>
                    <div className="tiny muted">{c.owner_name}{c.campaign_name ? ` · ${c.campaign_name}` : ""}</div>
                    <div className="row tiny" style={{ gap: 6 }}>
                      {c.platforms.map((p: string) => <span key={p} className="pill">{PLATFORM_LABEL[p]}</span>)}
                      {c.scheduled_at && <span className="muted">{fmtDateTime(c.scheduled_at)}</span>}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
      {adding && <NewContent onClose={() => setAdding(false)} onCreated={(id) => (setAdding(false), reload(), open(id))} />}
    </>
  );
}

function Calendar({ items, onOpen }: { items: any[]; onOpen: (id: string) => void }) {
  const today = todayYmd();
  const [month, setMonth] = useState(today.slice(0, 7));
  const [y, m] = month.split("-").map(Number) as [number, number];
  const first = new Date(Date.UTC(y, m - 1, 1));
  const start = new Date(first);
  start.setUTCDate(1 - first.getUTCDay());
  const days = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setUTCDate(start.getUTCDate() + i);
    return d.toISOString().slice(0, 10);
  });
  const shift = (n: number) => {
    const d = new Date(Date.UTC(y, m - 1 + n, 1));
    setMonth(d.toISOString().slice(0, 7));
  };
  const unscheduled = items.filter((c) => !c.scheduled_at && !["cancelled", "rejected"].includes(c.status));
  const tone: Record<string, string> = { approved: "var(--good)", scheduled: "var(--accent)", published: "var(--good)", publish_failed: "var(--bad)", in_review: "var(--info)", changes_requested: "var(--warn)" };
  return (
    <div className="stack" style={{ gap: 14 }}>
      <div className="row between">
        <strong>{first.toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" })}</strong>
        <div className="row">
          <button className="btn sm" onClick={() => shift(-1)}>Previous</button>
          <button className="btn sm" onClick={() => setMonth(today.slice(0, 7))}>Today</button>
          <button className="btn sm" onClick={() => shift(1)}>Next</button>
        </div>
      </div>
      <div className="cal">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="dow">{d}</div>)}
        {days.map((d) => (
          <div key={d} className={`day ${d.slice(0, 7) !== month ? "other" : ""} ${d === today ? "today" : ""}`}>
            <span className="num">{Number(d.slice(8))}</span>
            {items
              .filter((c) => c.scheduled_at?.slice(0, 10) === d && c.status !== "cancelled")
              .map((c) => (
                <div key={c.id} className="ev" style={{ borderLeftColor: tone[c.status] || "var(--line-strong)" }} onClick={() => onOpen(c.id)} title={`${c.title} · ${CONTENT_STATUS[c.status]!.label}`}>
                  {c.title}
                </div>
              ))}
          </div>
        ))}
      </div>
      {unscheduled.length > 0 && (
        <section className="card">
          <h2>No date yet</h2>
          <div className="list">
            {unscheduled.map((c) => (
              <div key={c.id} className="item clickable" onClick={() => onOpen(c.id)}>
                <span className="grow title">{c.title}</span>
                <StatusPill s={c.status} map={CONTENT_STATUS} />
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function PlatformPicker({ value, onChange, disabled }: { value: string[]; onChange: (v: string[]) => void; disabled?: boolean }) {
  return (
    <div className="row" style={{ gap: 12 }}>
      {PLATFORMS.map((p) => (
        <label key={p} className="check">
          <input type="checkbox" disabled={disabled} checked={value.includes(p)} onChange={(e) => onChange(e.target.checked ? [...value, p] : value.filter((x) => x !== p))} />
          {PLATFORM_LABEL[p]}
        </label>
      ))}
    </div>
  );
}

function NewContent({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const { me, toast } = useMe();
  const campaigns = useLoad<any[]>(can(me, "campaigns.view") ? "/api/campaigns" : null).data || [];
  const [f, setF] = useState({ title: "", caption: "", campaign_id: "", scheduled_at: "", platforms: [] as string[] });
  return (
    <Drawer title="New content" onClose={onClose}>
      <form
        className="form"
        onSubmit={async (e) => {
          e.preventDefault();
          try {
            const r = await api("/api/content", { method: "POST", body: f });
            toast("Draft saved");
            onCreated(r.id);
          } catch (e: any) {
            toast(e.message, true);
          }
        }}
      >
        <label className="field">Title<input className="input" required autoFocus value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></label>
        <label className="field">Caption<textarea className="input" value={f.caption} onChange={(e) => setF({ ...f, caption: e.target.value })} /></label>
        <div className="form-2">
          <label className="field">Campaign
            <select className="input" value={f.campaign_id} onChange={(e) => setF({ ...f, campaign_id: e.target.value })}>
              <option value="">None</option>
              {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label className="field">Planned date and time (Central)<input className="input" type="datetime-local" value={f.scheduled_at} onChange={(e) => setF({ ...f, scheduled_at: e.target.value })} /></label>
        </div>
        <div className="field">Platforms<PlatformPicker value={f.platforms} onChange={(platforms) => setF({ ...f, platforms })} /></div>
        <p className="muted small" style={{ margin: 0 }}>Add images or video after saving the draft.</p>
        <div className="row"><button className="btn primary">Save draft</button><button type="button" className="btn" onClick={onClose}>Cancel</button></div>
      </form>
    </Drawer>
  );
}

function Asset({ a, canRemove, onRemove }: { a: any; canRemove: boolean; onRemove: () => void }) {
  const src = `/api/attachments/${a.id}`;
  return (
    <div className="asset">
      <a href={src} target="_blank" rel="noreferrer">
        {a.mime.startsWith("image/") ? <img src={src} alt={a.filename} loading="lazy" /> : a.mime.startsWith("video/") ? <video src={src} muted preload="metadata" /> : <div className="file-doc">PDF</div>}
      </a>
      <div className="meta">
        <span title={a.filename}>{a.filename}</span>
        {canRemove && <button className="btn ghost sm" style={{ padding: "0 4px" }} onClick={onRemove} aria-label={`Remove ${a.filename}`}>Remove</button>}
      </div>
    </div>
  );
}

const ACTION_LABEL: Record<string, string> = {
  submit: "Send for review", withdraw: "Withdraw from review", approve: "Approve", request_changes: "Request changes", reject: "Reject",
  schedule: "Mark as scheduled", unschedule: "Unschedule", cancel: "Cancel this content", reopen: "Reopen as draft",
};
const NEEDS_NOTE = new Set(["request_changes", "reject"]);

function ContentDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const { me, toast } = useMe();
  const { data: c, error, reload, setData } = useLoad<any>(`/api/content/${id}`, [id]);
  const campaigns = useLoad<any[]>(can(me, "campaigns.view") ? "/api/campaigns" : null).data || [];
  const [viewV, setViewV] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [pub, setPub] = useState<{ platform: string; url: string; fail: string }>({ platform: "", url: "", fail: "" });
  const [newTask, setNewTask] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const captionRef = useRef<HTMLTextAreaElement>(null);

  if (!c) return <Drawer title="Content" onClose={onClose}><Loading error={error} /></Drawer>;
  const version = c.versions.find((v: any) => v.version === (viewV ?? c.current_version)) ?? c.versions[0];
  const isCurrent = version.version === c.current_version;
  const editable = c.can_edit && isCurrent && !["in_review", "published", "rejected", "cancelled"].includes(c.status);
  const rereviewWarn = ["approved", "scheduled", "publish_failed"].includes(c.status);

  const run = async (fn: () => Promise<any>, ok: string) => {
    setBusy(true);
    try {
      const r = await fn();
      if (r && r.versions) setData(r);
      else reload();
      toast(ok);
      return true;
    } catch (e: any) {
      toast(e.message, true);
      return false;
    } finally {
      setBusy(false);
    }
  };
  const act = (action: string, extra: any = {}) =>
    run(() => api(`/api/content/${id}/actions`, { method: "POST", body: { action, note, ...extra } }), `${ACTION_LABEL[action] || "Saved"}: done`).then((ok) => ok && setNote(""));
  const patch = (body: any, msg = "Saved") => run(() => api(`/api/content/${id}`, { method: "PATCH", body }), msg);

  const flowIndex = FLOW.indexOf(c.status === "changes_requested" ? "draft" : c.status === "publish_failed" ? "scheduled" : c.status);
  const decisions = c.actions.filter((a: string) => ["approve", "request_changes", "reject"].includes(a));
  const others = c.actions.filter((a: string) => !["submit", "approve", "request_changes", "reject", "mark_published", "mark_failed", "schedule"].includes(a));
  const pendingApproval = c.approvals.find((a: any) => a.status === "pending");
  const versionComments = c.comments.filter((x: any) => x.version === version.version);

  return (
    <Drawer
      title={c.title}
      onClose={onClose}
      sub={
        <div className="stack" style={{ gap: 8 }}>
          <div className="row"><StatusPill s={c.status} map={CONTENT_STATUS} /><span className="muted small">v{c.current_version}{c.approved_version ? ` · v${c.approved_version} approved` : ""}</span><Sample on={c.is_sample} /></div>
          <div className="steps">
            {FLOW.map((s, i) => (
              <span key={s} style={{ display: "contents" }}>
                {i > 0 && <i>›</i>}
                <span className={i < flowIndex ? "done" : i === flowIndex ? "now" : ""}>{CONTENT_STATUS[s]!.label}</span>
              </span>
            ))}
          </div>
        </div>
      }
    >
      {c.status === "changes_requested" && c.approvals[0]?.decision_note && (
        <div className="banner warn"><div><strong>{c.approvals[0].decided_by_name} asked for changes:</strong> {c.approvals[0].decision_note}</div></div>
      )}
      {c.status === "rejected" && <div className="banner bad">Rejected by {c.approvals[0]?.decided_by_name}: {c.approvals[0]?.decision_note}</div>}
      {c.status === "publish_failed" && <div className="banner bad">A post didn't go out. See Publishing below for the reason, then fix it and mark it published or reschedule.</div>}
      {pendingApproval && (
        <div className="banner info">
          Waiting on review of v{pendingApproval.version}, sent by {pendingApproval.requested_by_name} {ago(pendingApproval.created_at)}.
          {pendingApproval.requested_by === me.id && " Someone else needs to approve it."}
        </div>
      )}

      {(decisions.length > 0 || c.actions.includes("submit")) && (
        <section className="card stack">
          <h2>{decisions.length ? `Review v${c.current_version}` : "Ready for review?"}</h2>
          <textarea className="input" style={{ minHeight: 64 }} value={note} onChange={(e) => setNote(e.target.value)} placeholder={decisions.length ? "Feedback (needed to request changes or reject)" : "What changed (optional)"} />
          <div className="row">
            {c.actions.includes("submit") && <button className="btn primary" disabled={busy} onClick={() => act("submit")}>{ACTION_LABEL.submit}</button>}
            {decisions.includes("approve") && <button className="btn primary" disabled={busy} onClick={() => act("approve")}>Approve v{c.current_version}</button>}
            {decisions.includes("request_changes") && <button className="btn" disabled={busy || !note.trim()} onClick={() => act("request_changes")}>Request changes</button>}
            {decisions.includes("reject") && <button className="btn danger" disabled={busy || !note.trim()} onClick={() => act("reject")}>Reject</button>}
          </div>
        </section>
      )}

      <section className="card stack">
        <div className="row between">
          <h2>Caption and assets</h2>
          {c.versions.length > 1 && (
            <select className="input" style={{ width: "auto" }} value={version.version} onChange={(e) => setViewV(Number(e.target.value))} aria-label="Version">
              {c.versions.map((v: any) => <option key={v.version} value={v.version}>v{v.version}{v.version === c.current_version ? " (current)" : ""}{v.version === c.approved_version ? " · approved" : ""}</option>)}
            </select>
          )}
        </div>
        {!isCurrent && <div className="banner info">You're looking at an older version (v{version.version}).</div>}
        {editable && rereviewWarn && <div className="banner warn">This is approved. Changing the caption or assets starts a new version and sends it back to review.</div>}
        {version.change_note && <p className="small muted" style={{ margin: 0 }}>Change note: {version.change_note}</p>}
        {editable ? (
          <>
            <textarea ref={captionRef} className="input" key={version.version + ":" + c.updated_at} defaultValue={version.caption} style={{ minHeight: 120 }} />
            <div className="row">
              <button className="btn" disabled={busy} onClick={() => captionRef.current && captionRef.current.value !== version.caption && patch({ caption: captionRef.current.value }, "Caption saved")}>Save caption</button>
            </div>
          </>
        ) : (
          <p style={{ margin: 0, whiteSpace: "pre-wrap" }}>{version.caption || <span className="muted">No caption.</span>}</p>
        )}
        {version.attachments.length ? (
          <div className="assets">
            {version.attachments.map((a: any) => (
              <Asset key={a.id} a={a} canRemove={editable} onRemove={() => run(() => api(`/api/attachments/${a.id}`, { method: "DELETE" }), "Removed")} />
            ))}
          </div>
        ) : (
          <span className="muted small">No assets on this version.</span>
        )}
        {editable && (
          <div className="row">
            <input
              ref={fileRef}
              type="file"
              multiple
              accept="image/png,image/jpeg,image/gif,image/webp,image/heic,video/mp4,video/quicktime,application/pdf"
              style={{ display: "none" }}
              onChange={async (e) => {
                const files = [...(e.target.files || [])];
                if (!files.length) return;
                const fd = new FormData();
                fd.set("record_type", "content");
                fd.set("record_id", id);
                files.forEach((f) => fd.append("file", f));
                await run(() => api("/api/attachments", { method: "POST", body: fd }), `Uploaded ${files.length === 1 ? files[0]!.name : files.length + " files"}`);
                e.target.value = "";
              }}
            />
            <button className="btn" disabled={busy} onClick={() => fileRef.current?.click()}>Upload images or video</button>
            <span className="muted tiny">Images, MP4, MOV or PDF, up to 100 MB each</span>
          </div>
        )}
      </section>

      <section className="card stack">
        <h2>Comments on v{version.version}</h2>
        {versionComments.length ? (
          versionComments.map((x: any) => <div key={x.id} className="comment"><div className="who row" style={{ gap: 8 }}><Avatar id={x.author_id} name={x.author_name} hasPhoto={x.author_has_photo} size={22} />{x.author_name} <span className="muted tiny">{ago(x.created_at)}</span></div><p>{x.body}</p></div>)
        ) : (
          <span className="muted small">No comments on this version.</span>
        )}
        <form
          className="row"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!comment.trim()) return;
            await run(() => api("/api/comments", { method: "POST", body: { record_type: "content", record_id: id, version: version.version, body: comment } }), "Comment posted");
            setComment("");
          }}
        >
          <input className="input" style={{ flex: 1 }} value={comment} onChange={(e) => setComment(e.target.value)} placeholder={`Comment on v${version.version}`} />
          <button className="btn">Post</button>
        </form>
      </section>

      <section className="card stack">
        <h2>Details</h2>
        <div className="form-2">
          <label className="field">Planned date and time (Central)
            <input className="input" type="datetime-local" disabled={!c.can_edit || c.status === "published"} defaultValue={c.scheduled_at || ""} key={c.scheduled_at} onBlur={(e) => e.target.value !== (c.scheduled_at || "") && patch({ scheduled_at: e.target.value || null })} />
          </label>
          <label className="field">Campaign
            <select className="input" disabled={!c.can_edit} value={c.campaign_id || ""} onChange={(e) => patch({ campaign_id: e.target.value || null })}>
              <option value="">None</option>
              {campaigns.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
          </label>
        </div>
        <div className="field">Platforms<PlatformPicker value={c.platforms} disabled={!c.can_edit || c.status === "published"} onChange={(platforms) => patch({ platforms })} /></div>
        <p className="small muted" style={{ margin: 0 }}>Owner: {c.owner_name}. Changing the date, campaign or platforms doesn't need another review.</p>
        <ReleaseLink type="content" id={id} canEdit={c.can_edit} />
      </section>

      {(["approved", "scheduled", "publish_failed", "published"].includes(c.status)) && (
        <section className="card stack">
          <h2>Publishing</h2>
          <div className="banner info">No social account is connected yet. "Scheduled" means it's planned in your scheduling tool; "Published" needs the live post link.</div>
          {c.actions.includes("schedule") && (
            <div className="row"><button className="btn primary" disabled={busy} onClick={() => act("schedule")}>Mark as scheduled for {fmtDateTime(c.scheduled_at) || "…"}</button>{!c.scheduled_at && <span className="muted small">Set a date first.</span>}</div>
          )}
          {c.posts.length > 0 && (
            <table className="t">
              <thead><tr><th>Platform</th><th>Status</th><th>Detail</th></tr></thead>
              <tbody>
                {c.posts.map((p: any) => (
                  <tr key={p.platform}>
                    <td>{PLATFORM_LABEL[p.platform]}</td>
                    <td><Pill tone={p.status === "published" ? "good" : p.status === "failed" ? "bad" : p.status === "scheduled" ? "accent" : ""}>{p.status === "not_scheduled" ? "Not scheduled" : p.status[0].toUpperCase() + p.status.slice(1)}</Pill></td>
                    <td className="small">{p.status === "published" ? <><a href={p.post_url} target="_blank" rel="noreferrer">View post</a> <span className="muted">· recorded by hand</span></> : p.error ? <span style={{ color: "var(--bad)" }}>{p.error}</span> : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {(c.actions.includes("mark_published") || c.actions.includes("mark_failed")) && (
            <div className="stack">
              <div className="form-2">
                <label className="field">Platform
                  <select className="input" value={pub.platform} onChange={(e) => setPub({ ...pub, platform: e.target.value })}>
                    <option value="">Choose…</option>
                    {c.platforms.filter((p: string) => c.posts.find((x: any) => x.platform === p)?.status !== "published").map((p: string) => <option key={p} value={p}>{PLATFORM_LABEL[p]}</option>)}
                  </select>
                </label>
                <label className="field">Live post link<input className="input" type="url" placeholder="https://" value={pub.url} onChange={(e) => setPub({ ...pub, url: e.target.value })} /></label>
              </div>
              <div className="row">
                <button className="btn primary" disabled={busy || !pub.platform || !pub.url} onClick={() => act("mark_published", { platform: pub.platform, post_url: pub.url }).then(() => setPub({ platform: "", url: "", fail: "" }))}>Record as published</button>
                {c.actions.includes("mark_failed") && (
                  <>
                    <input className="input" style={{ flex: 1, minWidth: 160 }} placeholder="What went wrong" value={pub.fail} onChange={(e) => setPub({ ...pub, fail: e.target.value })} />
                    <button className="btn danger" disabled={busy || !pub.platform || !pub.fail} onClick={() => run(() => api(`/api/content/${id}/actions`, { method: "POST", body: { action: "mark_failed", platform: pub.platform, note: pub.fail } }), "Marked as failed")}>Mark failed</button>
                  </>
                )}
              </div>
            </div>
          )}
        </section>
      )}

      <section className="card stack">
        <div className="row between"><h2>Related tasks</h2><button className="btn sm" onClick={() => setNewTask(true)}>Add task</button></div>
        {c.linked_tasks.length ? <div className="list">{c.linked_tasks.map((t: any) => <TaskRow key={t.id} t={t} onOpen={(tid) => setQuery({ task: tid, content: null })} />)}</div> : <span className="muted small">None.</span>}
      </section>

      <section className="card stack">
        <h2>Approval history</h2>
        {c.approvals.length ? (
          <div className="timeline">
            {c.approvals.map((a: any) => (
              <div key={a.id}>
                <time>{ago(a.decided_at || a.created_at)}</time>
                <span>v{a.version}: {a.status === "pending" ? `waiting (sent by ${a.requested_by_name})` : `${a.status.replace("_", " ")} by ${a.decided_by_name || "—"}`}{a.decision_note ? `: "${a.decision_note}"` : ""}</span>
              </div>
            ))}
          </div>
        ) : (
          <span className="muted small">Not sent for review yet.</span>
        )}
        <h2 style={{ marginTop: 12 }}>Activity</h2>
        <div className="timeline">
          {c.activity.map((a: any) => <div key={a.id}><time>{ago(a.created_at)}</time><span>{a.actor_name || "System"}: {a.action.replace(/^content\./, "").replace(/_/g, " ")}{a.detail.to ? ` → ${CONTENT_STATUS[a.detail.to]?.label ?? a.detail.to}` : ""}</span></div>)}
        </div>
      </section>

      {others.length > 0 && (
        <div className="row">{others.map((a: string) => <button key={a} className={`btn sm ${a === "cancel" ? "danger" : ""}`} disabled={busy} onClick={() => (a !== "cancel" || confirm("Cancel this content?")) && act(a)}>{ACTION_LABEL[a]}</button>)}</div>
      )}
      {newTask && <NewTask link={{ type: "content", id, label: c.title }} onClose={() => setNewTask(false)} onCreated={() => (setNewTask(false), reload())} />}
    </Drawer>
  );
}

function Campaigns() {
  const { me, toast } = useMe();
  const route = useRoute();
  const { data, error, reload } = useLoad<any[]>("/api/campaigns");
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ name: "", goal: "", audience: "", starts_on: "", ends_on: "", status: "planning", channels: [] as string[], budget_planned: "" });
  const sel = route.q.get("campaign");
  return (
    <>
      <div className="row between">
        <p className="muted small" style={{ margin: 0 }}>Performance from Meta, Apple Ads and website analytics arrives in Stage 3.</p>
        {can(me, "campaigns.edit") && <button className="btn primary" onClick={() => setAdding(true)}>New campaign</button>}
      </div>
      {!data ? (
        <Loading error={error} />
      ) : data.length ? (
        <div className="grid">
          {data.map((c) => (
            <section key={c.id} className="card clickable" onClick={() => setQuery({ campaign: c.id })}>
              <div className="row between"><h2>{c.name}</h2><Pill tone={c.status === "active" ? "good" : ""}>{c.status[0].toUpperCase() + c.status.slice(1)}</Pill></div>
              <p className="sub">{c.owner_name}{c.starts_on ? ` · ${fmtDate(c.starts_on)} to ${fmtDate(c.ends_on) || "?"}` : ""}</p>
              {c.goal && <p className="small" style={{ margin: 0 }}>{c.goal}</p>}
              <div className="row" style={{ marginTop: 10 }}><Sample on={c.is_sample} />{c.channels.map((x: string) => <span key={x} className="pill">{PLATFORM_LABEL[x]}</span>)}</div>
            </section>
          ))}
        </div>
      ) : (
        <Empty>No campaigns yet.</Empty>
      )}
      {sel && <CampaignDrawer id={sel} onClose={() => setQuery({ campaign: null })} />}
      {adding && (
        <Drawer title="New campaign" onClose={() => setAdding(false)}>
          <form
            className="form"
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                const r = await api("/api/campaigns", { method: "POST", body: f });
                setAdding(false);
                reload();
                setQuery({ campaign: r.id });
                toast("Campaign saved");
              } catch (e: any) {
                toast(e.message, true);
              }
            }}
          >
            <label className="field">Name<input className="input" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
            <label className="field">Goal<textarea className="input" value={f.goal} onChange={(e) => setF({ ...f, goal: e.target.value })} /></label>
            <label className="field">Audience<input className="input" value={f.audience} onChange={(e) => setF({ ...f, audience: e.target.value })} /></label>
            <div className="form-2">
              <label className="field">Starts<input className="input" type="date" value={f.starts_on} onChange={(e) => setF({ ...f, starts_on: e.target.value })} /></label>
              <label className="field">Ends<input className="input" type="date" value={f.ends_on} onChange={(e) => setF({ ...f, ends_on: e.target.value })} /></label>
              <label className="field">Status
                <select className="input" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
                  {["planning", "active", "paused", "done"].map((s) => <option key={s} value={s}>{s[0]!.toUpperCase() + s.slice(1)}</option>)}
                </select>
              </label>
              {can(me, "finance.edit") && <label className="field">Planned budget (USD)<input className="input" type="number" min="0" value={f.budget_planned} onChange={(e) => setF({ ...f, budget_planned: e.target.value })} /></label>}
            </div>
            <div className="field">Channels<PlatformPicker value={f.channels} onChange={(channels) => setF({ ...f, channels })} /></div>
            <div className="row"><button className="btn primary">Save campaign</button></div>
          </form>
        </Drawer>
      )}
    </>
  );
}

function CampaignDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const { me } = useMe();
  const { data: c, error, reload } = useLoad<any>(`/api/campaigns/${id}`, [id]);
  const [newTask, setNewTask] = useState(false);
  if (!c) return <Drawer title="Campaign" onClose={onClose}><Loading error={error} /></Drawer>;
  return (
    <Drawer title={c.name} onClose={onClose} sub={<div className="row"><Pill tone={c.status === "active" ? "good" : ""}>{c.status}</Pill><Sample on={c.is_sample} /></div>}>
      <section className="card stack">
        <div className="form-2 small">
          <div><div className="muted tiny">Owner</div>{c.owner_name}</div>
          <div><div className="muted tiny">Dates</div>{c.starts_on ? `${fmtDate(c.starts_on)} to ${fmtDate(c.ends_on) || "?"}` : "Not set"}</div>
          {can(me, "finance.view") && <div><div className="muted tiny">Planned budget</div>{c.budget_planned == null ? <span className="muted">Not set</span> : `$${Number(c.budget_planned).toLocaleString()}`}</div>}
          <div><div className="muted tiny">Channels</div>{c.channels.map((x: string) => PLATFORM_LABEL[x]).join(", ") || "None"}</div>
        </div>
        {c.goal && <p style={{ margin: 0 }}><span className="muted small">Goal: </span>{c.goal}</p>}
        {c.audience && <p style={{ margin: 0 }}><span className="muted small">Audience: </span>{c.audience}</p>}
        <ReleaseLink type="campaign" id={id} canEdit={can(me, "campaigns.edit")} />
      </section>
      <section className="card stack">
        <h2>Performance</h2>
        <div className="banner info">Not connected. {c.performance.note}</div>
      </section>
      <section className="card stack">
        <h2>Content</h2>
        {c.content.length ? (
          <div className="list">
            {c.content.map((x: any) => (
              <div key={x.id} className="item clickable" onClick={() => setQuery({ campaign: null, tab: null, content: x.id })}>
                <span className="grow title">{x.title}</span>
                <span className="muted small hide-sm">{fmtDateTime(x.scheduled_at)}</span>
                <StatusPill s={x.status} map={CONTENT_STATUS} />
              </div>
            ))}
          </div>
        ) : (
          <span className="muted small">No content in this campaign yet.</span>
        )}
      </section>
      <section className="card stack">
        <div className="row between"><h2>Tasks</h2><button className="btn sm" onClick={() => setNewTask(true)}>Add task</button></div>
        {c.tasks.length ? <div className="list">{c.tasks.map((t: any) => <TaskRow key={t.id} t={t} onOpen={(tid) => setQuery({ campaign: null, task: tid })} />)}</div> : <span className="muted small">None.</span>}
      </section>
      {newTask && <NewTask link={{ type: "campaign", id, label: c.name }} onClose={() => setNewTask(false)} onCreated={() => (setNewTask(false), reload())} />}
    </Drawer>
  );
}

// Claude's read on marketing: the week's moves toward paying subscribers, built from the campaign plan snapshot and the portal.
function MarketingTake() {
  return (
    <AdviceTake
      page="marketing"
      ask="Ask Claude where to focus"
      intro="Claude reads the campaign plan, live subscriber numbers and the content here, then tells you what to do this week, what's at risk and what to let go. About 10 cents a run."
      footer={(data) =>
        data.context?.updated_at ? (
          <div className="tiny muted">
            Based on {data.context.sources?.length ? data.context.sources.map((x: any, i: number) => <span key={i}>{i > 0 && ", "}{x.href ? <a href={x.href} target="_blank" rel="noreferrer">{x.label}</a> : x.label}</span>) : "the campaign plan"}, as of {new Date(data.context.updated_at).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "America/Chicago" })}. The morning agent refreshes it.
          </div>
        ) : (
          <div className="tiny muted">No campaign plan snapshot yet. The morning agent adds one from your Claude docs.</div>
        )
      }
    />
  );
}
